/**
 * The word sets `/slowka` shows: eleven everyday topics, each a list of
 * Polish words with their Ukrainian and English translations.
 *
 * Copied by hand from shot lists `scripts/social-video/` already rendered
 * into real videos (`--format=word --topic="..."`, runs 2026-09-16/17) —
 * every pair here passed that pipeline's own check (`pairCheck`,
 * `pair-check.ts`), the independent reading of a shot list `shotlist.ts` runs
 * before a video is allowed to render. Nothing here is invented for the page;
 * it is what the videos already say. The runs themselves live under
 * `.sources/social-video/`, which is local and gitignored — this file is the
 * durable copy.
 *
 * `videoId` is set only for a set whose embed was checked to actually play —
 * YouTube's oEmbed answering 200 for it
 * (`https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=<id>`),
 * not the content plan's date or status column, and not another platform
 * being live. On 2026-09-24 the plan still read `заплановано` for Shorts that
 * were public, and those public ones had embedding switched off (oEmbed 401,
 * "playback on other websites has been disabled"); it was switched back on
 * that day and five sets now carry their clip. A private Short answers 403.
 */

export interface WordPair {
  /** The Polish word. */
  pl: string;
  /** Its Ukrainian translation. */
  uk: string;
  /** Its Russian translation. */
  ru: string;
  /** Its English translation, for the English pages. */
  en: string;
}

export interface WordSet {
  id: string;
  words: WordPair[];
  /** YouTube id of the published short, once one exists for this set. */
  videoId?: string;
}

export const WORD_SETS: WordSet[] = [
  {
    id: "shop",
    videoId: "cc8V229cOrI",
    words: [
      { pl: "kasa", uk: "каса", ru: "касса", en: "checkout" },
      { pl: "rabat", uk: "знижка", ru: "скидка", en: "discount" },
      { pl: "paragon", uk: "чек", ru: "чек", en: "receipt" },
      { pl: "reszta", uk: "решта", ru: "сдача", en: "change" },
      { pl: "reklamówka", uk: "пакет", ru: "пакет", en: "carrier bag" },
      { pl: "koszyk", uk: "кошик", ru: "корзина", en: "basket" },
      { pl: "kolejka", uk: "черга", ru: "очередь", en: "queue" },
      { pl: "waga", uk: "ваги", ru: "весы", en: "scales" },
      { pl: "promocja", uk: "акція", ru: "акция", en: "special offer" },
      { pl: "termin przydatności", uk: "термін придатності", ru: "срок годности", en: "best-before date" },
    ],
  },
  {
    id: "doctor",
    videoId: "aMzNtzDvHk8",
    words: [
      { pl: "wizyta", uk: "прийом", ru: "приём", en: "appointment" },
      { pl: "rejestracja", uk: "реєстрація", ru: "регистратура", en: "reception desk" },
      { pl: "skierowanie", uk: "направлення", ru: "направление", en: "referral" },
      { pl: "recepta", uk: "рецепт", ru: "рецепт", en: "prescription" },
      { pl: "objawy", uk: "симптоми", ru: "симптомы", en: "symptoms" },
      { pl: "badanie", uk: "обстеження", ru: "обследование", en: "examination" },
      { pl: "wyniki", uk: "результати", ru: "результаты", en: "test results" },
      { pl: "dawkowanie", uk: "дозування", ru: "дозировка", en: "dosage" },
      { pl: "zwolnienie", uk: "лікарняний", ru: "больничный", en: "sick note" },
      { pl: "ból", uk: "біль", ru: "боль", en: "pain" },
    ],
  },
  {
    id: "bank",
    videoId: "KUV0xfV4kus",
    words: [
      { pl: "konto", uk: "банківський рахунок", ru: "банковский счёт", en: "bank account" },
      { pl: "rachunek", uk: "рахунок до оплати", ru: "счёт к оплате", en: "bill" },
      { pl: "karta płatnicza", uk: "платіжна картка", ru: "платёжная карта", en: "payment card" },
      { pl: "bankomat", uk: "банкомат", ru: "банкомат", en: "cash machine" },
      { pl: "przelew", uk: "переказ", ru: "перевод", en: "transfer" },
      { pl: "wpłata", uk: "внесення коштів", ru: "внесение денег", en: "deposit" },
      { pl: "wypłata", uk: "зняття коштів", ru: "снятие денег", en: "withdrawal" },
      { pl: "saldo", uk: "залишок на рахунку", ru: "остаток на счёте", en: "balance" },
      { pl: "lokata", uk: "строковий депозит", ru: "срочный депозит", en: "term deposit" },
      { pl: "zlecenie stałe", uk: "постійне доручення", ru: "постоянное поручение", en: "standing order" },
    ],
  },
  {
    id: "transport",
    videoId: "jrUpOxfRJ8Q",
    words: [
      { pl: "przystanek", uk: "зупинка", ru: "остановка", en: "stop" },
      { pl: "bilet", uk: "квиток", ru: "билет", en: "ticket" },
      { pl: "kasownik", uk: "валідатор", ru: "валидатор", en: "ticket validator" },
      { pl: "peron", uk: "платформа", ru: "платформа", en: "platform" },
      { pl: "tramwaj", uk: "трамвай", ru: "трамвай", en: "tram" },
      { pl: "rozkład jazdy", uk: "розклад руху", ru: "расписание", en: "timetable" },
      { pl: "autobus", uk: "автобус", ru: "автобус", en: "bus" },
      { pl: "przesiadka", uk: "пересадка", ru: "пересадка", en: "change (of train or bus)" },
      { pl: "kierowca", uk: "водій", ru: "водитель", en: "driver" },
      { pl: "opóźnienie", uk: "затримка", ru: "задержка", en: "delay" },
    ],
  },
  {
    id: "work",
    words: [
      { pl: "umowa", uk: "договір", ru: "договор", en: "contract" },
      { pl: "etat", uk: "повна зайнятість", ru: "полная занятость", en: "full-time post" },
      { pl: "zmiana", uk: "зміна", ru: "смена", en: "shift" },
      { pl: "grafik", uk: "робочий графік", ru: "рабочий график", en: "rota" },
      { pl: "urlop", uk: "відпустка", ru: "отпуск", en: "leave" },
      { pl: "wypłata", uk: "зарплата", ru: "зарплата", en: "pay" },
      { pl: "nadgodziny", uk: "понаднормові години", ru: "сверхурочные часы", en: "overtime" },
      { pl: "przełożony", uk: "керівник", ru: "начальник", en: "manager" },
      { pl: "szkolenie", uk: "навчання", ru: "обучение", en: "training" },
      { pl: "podwyżka", uk: "підвищення зарплати", ru: "повышение зарплаты", en: "pay rise" },
    ],
  },
  {
    id: "office",
    words: [
      { pl: "wniosek", uk: "заява", ru: "заявление", en: "application" },
      { pl: "załącznik", uk: "додаток", ru: "приложение", en: "attachment" },
      { pl: "termin", uk: "крайній термін", ru: "крайний срок", en: "deadline" },
      { pl: "zaświadczenie", uk: "довідка", ru: "справка", en: "certificate" },
      { pl: "dowód osobisty", uk: "посвідчення особи", ru: "удостоверение личности", en: "ID card" },
      { pl: "adres zamieszkania", uk: "адреса проживання", ru: "адрес проживания", en: "home address" },
      { pl: "pełnomocnictwo", uk: "довіреність", ru: "доверенность", en: "power of attorney" },
      { pl: "opłata", uk: "збір", ru: "сбор", en: "fee" },
      { pl: "podpis", uk: "підпис", ru: "подпись", en: "signature" },
      { pl: "odbiór", uk: "отримання", ru: "получение", en: "collection" },
    ],
  },
  {
    id: "renovation",
    words: [
      { pl: "wiertarka", uk: "дриль", ru: "дрель", en: "drill" },
      { pl: "wkrętarka", uk: "шурупокрут", ru: "шуруповёрт", en: "screwdriver (power)" },
      { pl: "kołek", uk: "дюбель", ru: "дюбель", en: "wall plug" },
      { pl: "wiertło", uk: "свердло", ru: "сверло", en: "drill bit" },
      { pl: "poziomica", uk: "рівень", ru: "уровень", en: "spirit level" },
      { pl: "farba", uk: "фарба", ru: "краска", en: "paint" },
      { pl: "szpachla", uk: "шпатель", ru: "шпатель", en: "filler" },
      { pl: "pędzel", uk: "пензель", ru: "кисть", en: "paintbrush" },
      { pl: "fuga", uk: "затирка", ru: "затирка", en: "grout" },
      { pl: "wałek", uk: "валик", ru: "валик", en: "paint roller" },
    ],
  },
  {
    id: "weather",
    words: [
      { pl: "pogoda", uk: "погода", ru: "погода", en: "weather" },
      { pl: "deszcz", uk: "дощ", ru: "дождь", en: "rain" },
      { pl: "słońce", uk: "сонце", ru: "солнце", en: "sun" },
      { pl: "wiatr", uk: "вітер", ru: "ветер", en: "wind" },
      { pl: "chmura", uk: "хмара", ru: "облако", en: "cloud" },
      { pl: "mgła", uk: "туман", ru: "туман", en: "fog" },
      { pl: "śnieg", uk: "сніг", ru: "снег", en: "snow" },
      { pl: "burza", uk: "гроза", ru: "гроза", en: "thunderstorm" },
      { pl: "mżawka", uk: "мряка", ru: "морось", en: "drizzle" },
      { pl: "upał", uk: "спека", ru: "жара", en: "heat" },
    ],
  },
  {
    id: "restaurant",
    words: [
      { pl: "rezerwacja", uk: "бронювання", ru: "бронирование", en: "reservation" },
      { pl: "kelner", uk: "офіціант", ru: "официант", en: "waiter" },
      { pl: "przystawka", uk: "закуска", ru: "закуска", en: "starter" },
      { pl: "rachunek", uk: "рахунок", ru: "счёт", en: "bill" },
      { pl: "napiwek", uk: "чайові", ru: "чаевые", en: "tip" },
      { pl: "sztućce", uk: "столові прибори", ru: "столовые приборы", en: "cutlery" },
      { pl: "danie główne", uk: "основна страва", ru: "основное блюдо", en: "main course" },
      { pl: "zamówienie", uk: "замовлення", ru: "заказ", en: "order" },
      { pl: "paragon", uk: "чек", ru: "чек", en: "receipt" },
      { pl: "stolik", uk: "столик", ru: "столик", en: "table" },
    ],
  },
  {
    id: "hairdresser",
    videoId: "8EVS6DY4hIw",
    words: [
      { pl: "fryzjer", uk: "перукар", ru: "парикмахер", en: "hairdresser" },
      { pl: "strzyżenie", uk: "стрижка", ru: "стрижка", en: "haircut" },
      { pl: "grzywka", uk: "чубчик", ru: "чёлка", en: "fringe" },
      { pl: "końcówki", uk: "кінчики", ru: "кончики", en: "ends" },
      { pl: "odrost", uk: "відросле коріння", ru: "отросшие корни", en: "roots (regrowth)" },
      { pl: "farbowanie", uk: "фарбування", ru: "окрашивание", en: "colouring" },
      { pl: "pasemka", uk: "мелірування", ru: "мелирование", en: "highlights" },
      { pl: "suszarka", uk: "фен", ru: "фен", en: "hairdryer" },
      { pl: "prostownica", uk: "випрямляч", ru: "выпрямитель", en: "straighteners" },
      { pl: "lokówka", uk: "плойка", ru: "плойка", en: "curling tongs" },
    ],
  },
  {
    id: "gym",
    words: [
      { pl: "bieżnia", uk: "бігова доріжка", ru: "беговая дорожка", en: "treadmill" },
      { pl: "hantle", uk: "гантелі", ru: "гантели", en: "dumbbells" },
      { pl: "szatnia", uk: "роздягальня", ru: "раздевалка", en: "changing room" },
      { pl: "ręcznik", uk: "рушник", ru: "полотенце", en: "towel" },
      { pl: "karnet", uk: "абонемент", ru: "абонемент", en: "membership pass" },
      { pl: "ławeczka", uk: "лава для жиму", ru: "скамья для жима", en: "weight bench" },
      { pl: "mata", uk: "килимок", ru: "коврик", en: "mat" },
      { pl: "ciężar", uk: "вага", ru: "вес", en: "weight" },
      { pl: "rozgrzewka", uk: "розминка", ru: "разминка", en: "warm-up" },
      { pl: "prysznic", uk: "душ", ru: "душ", en: "shower" },
    ],
  },
];
